import { API_BASE_URL, getAuthHeader } from './api'
import {
  IntelligenceRunResponse,
  IntelligenceStatusResponse,
  PlanDetailResponse,
  ResearchPlan,
  ResearchDepth,
} from '@/types/intelligence'

export interface DepthOption {
  id: ResearchDepth
  name: string
  description: string
  time_estimate: string
  source_estimate: string
  lanes: number
}

export interface StartRunRequest {
  user_prompt: string
  persona_mode: string
  topic_id: string
  topic_description: string
  is_public?: boolean
  region?: string
  time_horizon?: string
  depth?: ResearchDepth
}

export class IntelligenceApiError extends Error {
  status: number
  detail?: unknown

  constructor(message: string, status: number, detail?: unknown) {
    super(message)
    this.name = 'IntelligenceApiError'
    this.status = status
    this.detail = detail
  }
}

function getRequiredAuthHeader(): Record<string, string> {
  const headers = getAuthHeader()
  if (!headers.Authorization) {
    throw new IntelligenceApiError('Your session expired. Please log in again.', 401)
  }
  return headers
}

async function buildApiError(response: Response, fallbackMessage: string): Promise<IntelligenceApiError> {
  const errorPayload = await response.json().catch(() => ({}))
  const detail = errorPayload?.detail ?? errorPayload
  const message =
    typeof detail === 'string'
      ? detail
      : typeof detail?.message === 'string'
        ? detail.message
        : typeof errorPayload?.message === 'string'
          ? errorPayload.message
          : fallbackMessage

  return new IntelligenceApiError(message, response.status, detail)
}

export async function startIntelligenceRun(payload: StartRunRequest): Promise<IntelligenceRunResponse> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getRequiredAuthHeader(),
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw await buildApiError(response, 'Failed to start research plan.')
  }

  return response.json()
}

export function createIntelligenceEventSource(url: string): EventSource {
  return new EventSource(url)
}

export async function confirmIntelligencePlan(
  reportId: string, 
  plan: ResearchPlan, 
  updatedPersonaMode?: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/plan/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getRequiredAuthHeader(),
    },
    body: JSON.stringify({ 
      report_id: reportId, 
      plan_json: plan,
      updated_persona_mode: updatedPersonaMode,
    }),
  })

  if (!response.ok) {
    throw await buildApiError(response, 'Failed to confirm plan.')
  }
}

export async function fetchIntelligencePlan(reportId: string): Promise<PlanDetailResponse> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/${reportId}/plan`, {
    headers: {
      ...getRequiredAuthHeader(),
    },
  })

  if (!response.ok) {
    throw await buildApiError(response, 'Plan not found.')
  }

  return response.json()
}

export async function fetchIntelligenceStatus(reportId: string): Promise<IntelligenceStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/${reportId}/status`, {
    headers: {
      ...getRequiredAuthHeader(),
    },
  })

  if (!response.ok) {
    throw await buildApiError(response, 'Unable to fetch status.')
  }

  return response.json()
}

export async function cancelIntelligenceReport(reportId: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/${reportId}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getRequiredAuthHeader(),
    },
  })

  if (!response.ok) {
    throw await buildApiError(response, 'Failed to cancel research.')
  }

  return response.json()
}

export async function fetchDepthOptions(): Promise<DepthOption[]> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/depth-options`, {
    headers: {
      ...getRequiredAuthHeader(),
    },
  })

  if (!response.ok) {
    // Return default options if API fails
    return [
      {
        id: 'standard',
        name: 'Deep Analysis',
        description: 'Thorough research with deep AI analysis for informed decisions',
        time_estimate: '15-30 minutes',
        source_estimate: '~60 sources',
        lanes: 4,
      },
      {
        id: 'maximum',
        name: 'Expert Report',
        description: 'Comprehensive analyst-grade intelligence for critical decisions',
        time_estimate: '45-75 minutes',
        source_estimate: '~120 sources',
        lanes: 6,
      },
    ]
  }

  const data = await response.json()
  return data.depths
}

// Persona types
export interface Persona {
  id: string
  label: string
  group: string
  description: string
  objectives: string[]
}

export interface PersonaGroup {
  group_id: string
  group_label: string
  personas: Persona[]
}

export async function fetchPersonas(): Promise<PersonaGroup[]> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/research/personas`, {
    headers: {
      ...getAuthHeader(),
    },
  })

  if (!response.ok) {
    // Return default personas if API fails
    return [
      {
        group_id: 'consultants',
        group_label: 'Consulting & Strategy',
        personas: [
          { id: 'management_consultant', label: 'Management Consultant', group: 'consultants', description: 'Board-ready strategy analysis', objectives: [] },
        ]
      },
      {
        group_id: 'finance_analysts',
        group_label: 'Finance & Investment',
        personas: [
          { id: 'equity_research', label: 'Equity Research Analyst', group: 'finance_analysts', description: 'Investment-grade research', objectives: [] },
        ]
      }
    ]
  }

  const data = await response.json()
  return data.persona_groups
}

export {}

